import { createSlice } from '@reduxjs/toolkit';

import {
    ORDER_STATUS,
    POSITION_MARGIN_TYPES,
    POSITION_SIDE,
    PRICE_TYPE,
    SOCKET_TOPICS,
    TRADE_TYPE,
} from 'src/common/constants';
import { errorHandler, roundNumber } from 'src/common/helpers';
import {
    FundingRate,
    FutureSymbol,
    DerivativeAsset,
    Position,
    ActiveOrder,
    ConditionalOrder,
    FutureSymbolSettings,
    TradeOrder,
    MyTrade,
    FutureTradeAble,
    Toast,
    PositionStopProfitLoss,
    Leverage,
} from 'src/types';

export const callTypes = {
    list: 'list',
    action: 'action',
};

interface DerivativePageState {
    fundingRate: FundingRate;
    loading: boolean;
    error?: string;
    currentSymbol: FutureSymbol;
    assets: DerivativeAsset;
    symbolSettings: FutureSymbolSettings;
    currentSymbolPositions: Array<Position>;
    positions: Array<Position>;
    activeOrders: Array<ActiveOrder>;
    conditionalOrders: Array<ConditionalOrder>;
    tradeOrders: Array<TradeOrder>;
    myTrades: Array<MyTrade>;
    orderCreated: number;
    tradeable: FutureTradeAble[];
    transferAbleAmount: string | number;
    createOrderPrice: string;
    tvWidgetRef: any;
    toast: Toast;
    positionTrigger: string;
    longPositionStopProfitLoss: PositionStopProfitLoss;
    shortPositionStopProfitLoss: PositionStopProfitLoss;
    leverage: Leverage;
}

const initialState: DerivativePageState = {
    currentSymbol: {} as FutureSymbol,
    assets: {} as DerivativeAsset,
    symbolSettings: {
        symbolId: '',
        orderFee: {
            symbolId: '',
            symbolName: '',
            takerFeeRate: '',
            makerFeeRate: '',
        },
        orderSetting: {
            isPassiveOrder: false,
            timeInForce: '',
            isConfirm: true,
        },
        riskLimit: [
            {
                riskLimitId: '',
                riskLimitAmount: '',
                maintainMargin: '',
                initialMargin: '',
                side: '',
            },
            {
                riskLimitId: '',
                riskLimitAmount: '',
                maintainMargin: '',
                initialMargin: '',
                side: '',
            },
        ],
    },
    currentSymbolPositions: [] as Array<Position>, // TODO:: remove currentSymbolPositions. if needed get current symbol position from positions state instead of API call
    positions: [] as Array<Position>,
    orderCreated: 0,
    activeOrders: [] as Array<ActiveOrder>,
    conditionalOrders: [] as Array<ConditionalOrder>,
    tradeOrders: [] as Array<TradeOrder>,
    myTrades: [] as Array<MyTrade>,
    tradeable: [] as FutureTradeAble[],
    transferAbleAmount: 0,
    createOrderPrice: '',
    tvWidgetRef: null,
    toast: {} as Toast,
    positionTrigger: '',
    longPositionStopProfitLoss: {} as PositionStopProfitLoss,
    shortPositionStopProfitLoss: {} as PositionStopProfitLoss,
    leverage: { leverageLong: 0, leverageShort: 0 } as Leverage,
} as DerivativePageState;

export const derivativePageSlice = createSlice({
    name: 'derivativePage',
    initialState,
    reducers: {
        // eslint-disable-next-line no-unused-vars
        startCall: (state, action) => {
            state.error = undefined;
            state.loading = true;
        },
        // eslint-disable-next-line no-unused-vars
        callEnded: (state, action) => {
            state.loading = false;
        },
        catchError: (state, action) => {
            state.loading = false;
            const error = errorHandler(action.payload);
            if (error) {
                state.error = error.title;
                state.toast = error;
            }
        },
        fundingRateFetched: (state, action) => {
            state.loading = false;
            state.fundingRate = action.payload;
        },
        saveCurrentSymbol: (state, action) => {
            state.currentSymbol = action.payload;
            localStorage.setItem('currentFutureSymbol', action.payload.symbolId);
        },
        orderCreated: (state, action) => {
            const { order } = action.payload;
            state.loading = false;
            state.orderCreated += 1;
            if (
                order.status === ORDER_STATUS.NEW ||
                order.status === ORDER_STATUS.PARTIALLY_FILLED
            ) {
                const orders = filterOrders(order, state.activeOrders);
                state.activeOrders = orders as ActiveOrder[];
                state.tradeOrders = filterOrders(order, state.tradeOrders);
            } else if (
                order.status === ORDER_STATUS.FILLED ||
                order.status === ORDER_STATUS.CANCELED
            ) {
                state.activeOrders = state.activeOrders.filter(
                    (currentOrder) => currentOrder.orderId !== order.orderId,
                );
                state.tradeOrders = filterOrders(order, state.tradeOrders);
            } else if (order.status === ORDER_STATUS.ORDER_NEW) {
                const orders = filterOrders(order, state.conditionalOrders);
                state.conditionalOrders = orders as ConditionalOrder[];
            }
            const values = {
                quantity: order.origQty,
                name: order.symbolId.replace(/-.*/, ''),
                price: order.priceType === PRICE_TYPE.MARKET ? 'market' : order.price,
            };

            state.toast = {
                id: new Date().getTime().toString(),
                title: 'order_submitted',
                description:
                    order.side === TRADE_TYPE.BUY_OPEN || order.side === TRADE_TYPE.BUY_CLOSE
                        ? 'order_submitted_open_long_description'
                        : order.side === TRADE_TYPE.SELL_OPEN ||
                          order.side === TRADE_TYPE.SELL_CLOSE
                        ? 'order_submitted_open_short_description'
                        : '',
                type: 'success',
                values,
            };
        },
        availableAssetsFetched: (state, action) => {
            state.assets =
                action.payload.find(
                    (asset: DerivativeAsset) => asset.tokenId === state.currentSymbol.quoteTokenId,
                ) || ({} as DerivativeAsset);
        },
        symbolSettingsFetched: (state, action) => {
            state.loading = false;
            state.symbolSettings = action.payload;
        },
        symbolPositionsFetched: (state, action) => {
            state.loading = false;
            state.currentSymbolPositions = action.payload;
        },
        fetchPositions: (state, action) => {
            state.loading = false;
            state.positions = action.payload;
            state.positionTrigger = 'positions_fetched';
        },
        fetchActiveOrders: (state, action) => {
            state.loading = false;
            state.activeOrders = action.payload;
        },
        fetchConditionalOrders: (state, action) => {
            state.loading = false;
            state.conditionalOrders = action.payload;
        },
        fetchTradeOrders: (state, action) => {
            state.loading = false;
            state.tradeOrders = action.payload;
        },
        fetchMyTrades: (state, action) => {
            state.loading = false;
            state.myTrades = action.payload;
        },
        activeOrderCanceled: (state, action) => {
            const canceledOrder: ActiveOrder = action.payload;
            state.activeOrders = state.activeOrders.filter(
                (order: ActiveOrder) => order.orderId !== canceledOrder.orderId,
            );
            state.loading = false;
            state.toast = {
                id: new Date().getTime().toString(),
                title: 'order_cancelled',
                type: 'success',
            };
        },
        conditionalOrderCanceled: (state, action) => {
            const canceledOrder: ConditionalOrder = action.payload;
            state.conditionalOrders = state.conditionalOrders.filter(
                (order: ConditionalOrder) => order.orderId !== canceledOrder.orderId,
            );
            state.loading = false;
            state.toast = {
                id: new Date().getTime().toString(),
                title: 'order_cancelled',
                type: 'success',
            };
        },
        updateTvWidgetRef(state, action) {
            state.tvWidgetRef = action.payload;
        },
        updateCreateOrderPrice(state, action) {
            state.createOrderPrice = action.payload;
        },
        socketUserDataReceived: (state, action) => {
            const { data, topic } = action.payload;
            if (data) {
                switch (topic) {
                    case SOCKET_TOPICS.FUTURES_ORDER:
                        data.forEach((order: TradeOrder | ActiveOrder) => {
                            if (
                                order.status === ORDER_STATUS.NEW ||
                                order.status === ORDER_STATUS.PARTIALLY_FILLED
                            ) {
                                const orders = filterOrders(order, state.activeOrders);
                                state.activeOrders = orders as ActiveOrder[];
                                state.conditionalOrders = state.conditionalOrders.filter(
                                    (currentOrder) =>
                                        currentOrder.clientOrderId !== order.clientOrderId,
                                );
                            } else if (
                                order.status === ORDER_STATUS.FILLED ||
                                order.status === ORDER_STATUS.CANCELED
                            ) {
                                state.activeOrders = state.activeOrders.filter(
                                    (currentOrder) => currentOrder.orderId !== order.orderId,
                                );
                            }
                            state.tradeOrders = filterOrders(order, state.tradeOrders);
                            if (
                                order.status === ORDER_STATUS.FILLED ||
                                order.status === ORDER_STATUS.PARTIALLY_FILLED
                            ) {
                                const values = {
                                    quantity: order.executedQty,
                                    name: order.symbolId.replace(/-.*/, ''),
                                    price:
                                        order.priceType === PRICE_TYPE.MARKET
                                            ? 'market'
                                            : order.avgPrice,
                                };
                                state.toast = {
                                    id: new Date().getTime().toString(),
                                    title:
                                        order.status === ORDER_STATUS.FILLED
                                            ? 'order_filled'
                                            : 'order_partially_filled',
                                    description:
                                        order.side === TRADE_TYPE.BUY_OPEN ||
                                        order.side === TRADE_TYPE.BUY_CLOSE
                                            ? 'order_filled_open_long_description'
                                            : order.side === TRADE_TYPE.SELL_OPEN ||
                                              order.side === TRADE_TYPE.SELL_CLOSE
                                            ? 'order_filled_open_short_description'
                                            : '',
                                    type: 'info',
                                    values,
                                };
                            }
                        });
                        state.activeOrders.sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
                        state.tradeOrders.sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
                        break;
                    case SOCKET_TOPICS.FUTURES_POSITION:
                        {
                            if (data.length < state.positions.length) {
                                const removePositions = state.positions.filter(
                                    (position) =>
                                        !data.some(
                                            (pos: Position) =>
                                                pos.positionId === position.positionId,
                                        ),
                                );
                                removePositions.forEach((removePosition: Position) => {
                                    if (removePosition.isLong === POSITION_SIDE.LONG) {
                                        delete state.longPositionStopProfitLoss[
                                            removePosition.symbolId
                                        ];
                                    } else {
                                        delete state.shortPositionStopProfitLoss[
                                            removePosition.symbolId
                                        ];
                                    }
                                });
                            }

                            state.positions = data;
                        }
                        break;
                    case SOCKET_TOPICS.FUTURES_TRADEABLE:
                        state.tradeable = data;
                        break;
                    default:
                        break;
                }
            }
        },
        savePositionStopLoss(state, action) {
            state.loading = false;
            const payload = action.payload;
            if (payload.isLong.toString() == POSITION_SIDE.LONG) {
                state.longPositionStopProfitLoss[payload.symbolId] = payload;
            } else {
                state.shortPositionStopProfitLoss[payload.symbolId] = payload;
            }
            state.positionTrigger = 'position_stop_loss_added';
            if (payload.isSet) {
                state.toast = {
                    id: new Date().getTime().toString(),
                    title: 'tp_sl_update_success',
                    type: 'success',
                };
            }
        },
        cancelPositionStopLoss(state, action) {
            const payload = action.payload;
            if (payload.isLong === POSITION_SIDE.LONG) {
                state.longPositionStopProfitLoss[payload.symbolId] = payload;
            } else {
                state.shortPositionStopProfitLoss[payload.symbolId] = payload;
            }
            state.positionTrigger = 'position_stop_loss_cancel';
            state.toast = {
                id: new Date().getTime().toString(),
                title: 'tp_sl_update_cancelled',
                type: 'success',
            };
        },
        removeLoggedInUserData(state) {
            state.positions = [] as Array<Position>;
            state.activeOrders = [] as Array<ActiveOrder>;
            state.conditionalOrders = [] as Array<ConditionalOrder>;
            state.tradeOrders = [] as Array<TradeOrder>;
            state.myTrades = [] as Array<MyTrade>;
            state.positionTrigger = '';
            state.longPositionStopProfitLoss = {} as PositionStopProfitLoss;
            state.shortPositionStopProfitLoss = {} as PositionStopProfitLoss;
        },
        positionMarginModify(state, action) {
            state.loading = false;
            if (action.payload.status) {
                state.toast = {
                    id: new Date().getTime().toString(),
                    title:
                        action.payload.type === POSITION_MARGIN_TYPES.INCREASE
                            ? 'added_margin_successful'
                            : 'removed_margin_successful',
                    type: 'success',
                };
            }
        },
        showNotification(state, action) {
            state.toast = {
                id: new Date().getTime().toString(),
                title: action.payload.title,
                type: action.payload.type,
                description: action.payload.description && action.payload.description,
            };
        },
        leverageFetched: (state, action) => {
            state.loading = false;
            state.leverage = {
                leverageLong: roundNumber(Number(action.payload.leverageLong), 1),
                leverageShort: roundNumber(Number(action.payload.leverageShort), 1),
            };
        },
    },
});

function filterOrders(order: ActiveOrder | TradeOrder, orders: TradeOrder[] | ActiveOrder[]) {
    const orderExist = orders.some((currentOrder) => currentOrder.orderId === order.orderId);
    if (orderExist) {
        return orders.map((currentOrder) => {
            if (currentOrder.orderId === order.orderId) {
                return order;
            }
            return currentOrder;
        });
    } else {
        return [order as TradeOrder, ...orders];
    }
}
